import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import DashboardService from "../../services/DashboardService";
import AuthService from "../../services/AuthService";
import CallsLineGraph from "./CallsLineGraph";
import axios from "axios";
import PieChart from "./PieChart";
import WelcomePopup from "./WelcomePopup";
import EmployeeWelcomePopup from "./EmployeeWelcomePopup";
import { FaUser } from "react-icons/fa";
import RegisterUserService from "../../services/RegisterUserService";
import LeadService from "../../services/LeadService";
import LeadStatusService from "../../services/LeadStatusService";
import LeadsAssignedService from "../../services/LeadsAssignedService";
import AttendanceService from "../../services/AttendanceService";
import { ATTENDANCE_STATUSES, getAttendanceStatus, fmtTime } from "../../constants/attendanceConstants";
import CrmWorkflowService from "../../services/CrmWorkflowService";
import { getDealStage } from "../../constants/crmWorkflowConstants";
import { fmtPrice } from "../../constants/propertyConstants";
import { Toast } from "primereact/toast";

const PREVIEW_ROWS = 5;
const MODAL_HOT_LIMIT = 80;
const MODAL_ACTIVITY_LIMIT = 120;

function rollingSevenDayBounds() {
  const end = new Date();
  end.setHours(23, 59, 59, 999);
  const start = new Date(end);
  start.setDate(start.getDate() - 6);
  start.setHours(0, 0, 0, 0);
  const iso = (d) => d.toISOString().split("T")[0];
  return { startStr: iso(start), endStr: iso(end), start, end };
}

function isInRollingWeek(dateVal, weekStart, weekEnd) {
  if (!dateVal) return false;
  const t = new Date(dateVal).getTime();
  return t >= weekStart.getTime() && t <= weekEnd.getTime();
}

function useQuery() {
  const { search } = useLocation();
  return React.useMemo(() => new URLSearchParams(search), [search]);
}

function Dashboard() {
  const [isLoggedIn, setIsLoggedIn] = useState(AuthService.isAuthenticated());
  const navigate = useNavigate();
  const [leadCount, setLeadCount] = useState(0);
  const [leadAssignedCount, setLeadAssignedCount] = useState(0);
  const [interestedCount, setInterestedCount] = useState(0);
  const [upcomingVisitCount, setUpcomingVisitCount] = useState(0);
  const [phoneNotPickedCount, setPhoneNotPickedCount] = useState(0);
  const [invalidNumberCount, setInvalidNumberCount] = useState(0);
  const [callBackLaterCount, setCallBackLaterCount] = useState(0);
  const [completeCount, setCompleteCount] = useState(0);
  const [totalCallsCount, setTotalCallsCount] = useState(0);
  const [visitedCount, setVisitedCount] = useState(0);
  const [hotClientCount, setHotClientCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const queryParam = useQuery();
  const [UserId] = useState(() => AuthService.resolveScopedUserId(queryParam.get("id")));
  const userRole = AuthService.getLoggedInRoleId();
  const roleNumeric = parseInt(String(userRole), 10);
  /** Employees (4) omit deal pipeline blocks and Deals & Performance on the dashboard */
  const canViewDealDashboard = roleNumeric !== 4;

  const currentUserId = AuthService.getLoggedInUserId();

  const [selectedEmployee, setSelectedEmployee] = useState(currentUserId);

  const [employees, setEmployees] = useState([]);

  const [showPopup, setShowPopup] = useState(false);

  const currentDate = new Date();
  const formattedCurrentDate = currentDate.toISOString().split("T")[0];
  const startOfMonth = new Date(
    currentDate.getFullYear(),
    currentDate.getMonth(),
    1
  );
  const formattedStartOfMonth = startOfMonth.toISOString().split("T")[0];

  const yesterday = new Date(currentDate);
  yesterday.setDate(currentDate.getDate() - 1);
  const formattedyesterday = yesterday.toISOString().split("T")[0];

  const startOfYear = new Date(currentDate.getFullYear(), 0, 1);
  const formattedStartOfYear = startOfYear.toISOString().split("T")[0];

  const [startDate, setStartDate] = useState(formattedCurrentDate);
  const [endDate, setEndDate] = useState(formattedCurrentDate);
  const [quickRange, setQuickRange] = useState("today");

  const tenDaysAgo = new Date();
  tenDaysAgo.setDate(tenDaysAgo.getDate() - 10);
  const formattedTenDaysAgo = tenDaysAgo.toISOString().split("T")[0];

  const [leadStatus, setLeadStatus] = useState([]);
  const [pipelineStages, setPipelineStages] = useState([]);
  const [stageCounts, setStageCounts] = useState({});
  const ownClientId = AuthService.getLoggedInClientId();
  const [clients, setClients] = useState([]);
  const [selectedClientId, setSelectedClientId] = useState(ownClientId);
  const clientId = selectedClientId || ownClientId;

  // ── New: Action Center + Funnel + Follow-Up list ──
  const [conversionData, setConversionData] = useState(null);
  const [pendingCount, setPendingCount] = useState(0);
  const [todayFollowUps, setTodayFollowUps] = useState([]);

  // ── New: Hot Leads + Activity Feed + Leaderboard ──
  const [hotLeads, setHotLeads] = useState([]);
  const [recentActivity, setRecentActivity] = useState([]);
  const [employeePerf, setEmployeePerf] = useState([]);

  // ── Attendance Analytics ──
  const [todayAttendance, setTodayAttendance] = useState([]);

  // ── CRM Deals (transactions) ──
  const toastRef = useRef(null);
  const [pipelineDeals, setPipelineDeals] = useState([]);
  const [dealsLoading, setDealsLoading] = useState(false);
  /** active | won | lost | channel | all */
  const [dealFilter, setDealFilter] = useState("active");

  /** null | 'hot' | 'activity' | 'performers' — full list for past 7 days */
  const [dashModal, setDashModal] = useState(null);
  const [dashModalLoading, setDashModalLoading] = useState(false);
  const [dashModalHot, setDashModalHot] = useState([]);
  const [dashModalActivity, setDashModalActivity] = useState([]);
  const [dashModalPerf, setDashModalPerf] = useState([]);

  useEffect(() => {
    const hasShownPopup = localStorage.getItem("hasShownPopup");
    if (!hasShownPopup) {
      setShowPopup(true);
      localStorage.setItem("hasShownPopup", "true");
    }
  }, []);

  const handleClosePopup = () => {
    setShowPopup(false);
  };

  useEffect(() => {
    const fetchLeadStatus = async () => {
      try {
        const leadstatus = await LeadService.getAllLeadStatus();
        setLeadStatus(leadstatus);
      } catch (error) {
        console.error("Error fetching lead status:", error);
      }
    };

    fetchLeadStatus();
  }, [UserId]);

  useEffect(() => {
    const fetchPipelineStages = async () => {
      try {
        const stages = await LeadStatusService.getPipelineStagesByClientId(clientId);
        setPipelineStages(stages);
      } catch (error) {
        console.error("Error fetching pipeline stages:", error);
      }
    };
    fetchPipelineStages();
  }, [clientId]);

  useEffect(() => {
    async function fetchEmployees() {
      try {
        const users = await RegisterUserService.getEmployessByRoleId(4);
        setEmployees(users);
        if (users.length > 0) {
          setSelectedEmployee(currentUserId);
        }
      } catch (error) {
        console.error("Error fetching employees:", error);
      }
    }
    fetchEmployees();
  }, [currentUserId]);

  useEffect(() => {
    const token = AuthService.getToken();
    if (token) {
      axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
      setIsLoggedIn(true);
    }
  }, [isLoggedIn]);

  /* fetch all clients for the SuperAdmin client picker */
  useEffect(() => {
    if (userRole != 1) return; // 1 = SuperAdmin
    import('../../../SuperAdmin/services/ClientService').then((mod) => {
      mod.default.getAllCLients().then((list) => {
        setClients(list);
        if (list.length > 0 && !selectedClientId) setSelectedClientId(list[0].id);
      }).catch(() => {});
    });
  }, [userRole]);

  useEffect(() => {
    handleGetDashboardCards();
  }, [UserId, startDate, endDate, clientId]);

  useEffect(() => {
    if (!clientId) return;
    DashboardService.getConversionReport(clientId, startDate, endDate)
      .then(setConversionData)
      .catch(() => {});
  }, [clientId, startDate, endDate]);

  useEffect(() => {
    if (!clientId) {
      setPendingCount(0);
      setTodayFollowUps([]);
      return;
    }
    LeadsAssignedService.getPendingFollowupCount(UserId, clientId)
      .then(setPendingCount)
      .catch(() => {});
    LeadsAssignedService.getTodayFollowUps(UserId, clientId)
      .then(setTodayFollowUps)
      .catch(() => {});
  }, [UserId, clientId]);

  useEffect(() => {
    if (!clientId) return;
    DashboardService.getHotLeads(clientId, 12).then(setHotLeads).catch(() => {});
    DashboardService.getRecentActivity(clientId, 15).then(setRecentActivity).catch(() => {});
    DashboardService.getEmployeePerformance(clientId, startDate, endDate)
      .then(setEmployeePerf).catch(() => {});
  }, [clientId, startDate, endDate]);

  useEffect(() => {
    if (!dashModal || !clientId) return;
    let cancelled = false;
    const { startStr, endStr, start, end } = rollingSevenDayBounds();
    setDashModalHot([]);
    setDashModalActivity([]);
    setDashModalPerf([]);
    setDashModalLoading(true);
    (async () => {
      try {
        if (dashModal === "hot") {
          const rows = await DashboardService.getHotLeads(clientId, MODAL_HOT_LIMIT);
          if (cancelled) return;
          setDashModalHot(
            rows.filter((r) => isInRollingWeek(r.lastActivity ?? r.LastActivity, start, end))
          );
        } else if (dashModal === "activity") {
          const rows = await DashboardService.getRecentActivity(clientId, MODAL_ACTIVITY_LIMIT);
          if (cancelled) return;
          setDashModalActivity(
            rows.filter((r) => isInRollingWeek(r.activityTime ?? r.ActivityTime, start, end))
          );
        } else if (dashModal === "performers") {
          const rows = await DashboardService.getEmployeePerformance(clientId, startStr, endStr);
          if (cancelled) return;
          const sorted = [...rows].sort(
            (a, b) =>
              (b.totalDealDone ?? b.TotalDealDone ?? 0) - (a.totalDealDone ?? a.TotalDealDone ?? 0)
          );
          setDashModalPerf(sorted);
        }
      } catch {
        if (!cancelled) {
          if (dashModal === "hot") setDashModalHot([]);
          if (dashModal === "activity") setDashModalActivity([]);
          if (dashModal === "performers") setDashModalPerf([]);
        }
      } finally {
        if (!cancelled) setDashModalLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [dashModal, clientId]);

  useEffect(() => {
    if (!dashModal) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setDashModal(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [dashModal]);

  // fetch today's team attendance for manager/admin dashboard
  useEffect(() => {
    if (!clientId || userRole === 4) return; // skip for employees
    AttendanceService.getTeamAttendance(clientId, new Date())
      .then(setTodayAttendance)
      .catch(() => {});
  }, [clientId, userRole]);

  const reloadPipelineDeals = useCallback(async () => {
    if (!clientId) return;
    setDealsLoading(true);
    try {
      const list = await CrmWorkflowService.getDealsByClient(clientId);
      setPipelineDeals(Array.isArray(list) ? list : []);
    } catch {
      setPipelineDeals([]);
      toastRef.current?.show({
        severity: "warn",
        summary: "Deals",
        detail: "Could not load transactions for this client.",
        life: 3500,
      });
    } finally {
      setDealsLoading(false);
    }
  }, [clientId]);

  useEffect(() => {
    if (!canViewDealDashboard) return;
    reloadPipelineDeals();
  }, [reloadPipelineDeals, canViewDealDashboard]);

  async function handleGetDashboardCards() {
    setLoading(true);
    try {
      let userId;
      if (selectedEmployee == currentUserId) {
        userId = UserId;
      } else {
        userId = selectedEmployee;
      }

      const response = await DashboardService.getDashboardCards(
        selectedEmployee,
        clientId,
        startDate,
        endDate
      );
      setLeadCount(response.leadCount || 0);
      setInterestedCount(response.interestedCount || 0);
      setUpcomingVisitCount(response.upcomingVisitCount || 0);
      setPhoneNotPickedCount(response.phoneNotPickedCount || 0);
      setInvalidNumberCount(response.invalidNumberCount || 0);
      setCallBackLaterCount(response.callBackLaterCount || 0);
      setCompleteCount(response.completeCount || 0);
      setTotalCallsCount(response.totalCallsCount || 0);
      setLeadAssignedCount(response.leadAssignedCount || 0);
      setVisitedCount(response.visitedCount || 0);
      setHotClientCount(response.hotClientCount || 0);
      setStageCounts(response.stageCounts || {});
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  }

  const handleDateChange = (event) => {
    const value = event.target.value;
    setQuickRange(value || "custom");
    const today = formattedCurrentDate;
    const currentYear = new Date().getFullYear();

    if (value === "today") {
      setStartDate(today);
      setEndDate(today);
    } else if (value === "yesterday") {
      const yesterday = new Date();
      yesterday.setDate(yesterday.getDate() - 1);
      const formattedYesterday = yesterday.toISOString().split("T")[0];
      setStartDate(formattedYesterday);
      setEndDate(formattedYesterday);
    } else if (value === "last10days") {
      setStartDate(formattedTenDaysAgo);
      setEndDate(today);
    } else if (value === "thisMonth") {
      setStartDate(formattedStartOfMonth);
      setEndDate(today);
    } else if (value === "lastMonth") {
      const lastMonthDate = new Date();
      lastMonthDate.setMonth(lastMonthDate.getMonth() - 1);
      lastMonthDate.setDate(1);
      const formattedStartOfLastMonth = lastMonthDate
        .toISOString()
        .split("T")[0];

      lastMonthDate.setMonth(lastMonthDate.getMonth() + 1);
      lastMonthDate.setDate(0);
      const formattedEndOfLastMonth = lastMonthDate.toISOString().split("T")[0];

      setStartDate(formattedStartOfLastMonth);
      setEndDate(formattedEndOfLastMonth);
    } else if (value === "thisYear") {
      const startOfYear = `${currentYear}-01-01`;
      setStartDate(startOfYear);
      setEndDate(today);
    } else if (value === "lastYear") {
      const lastYear = currentYear - 1;
      const startOfLastYear = `${lastYear}-01-01`;
      const endOfLastYear = `${lastYear}-12-31`;
      setStartDate(startOfLastYear);
      setEndDate(endOfLastYear);
    }
  };

  const Role = {
    SUPERADMIN: { roleId: 1, roleName: "SuperAdmin" },
    ADMIN: { roleId: 2, roleName: "Admin" },
    EMPLOYEE: { roleId: 4, roleName: "Employee" },
  };

  /* ── shared inline helpers ── */
  const sel = {
    padding: '8px 12px', border: '1.5px solid #e9ecef', borderRadius: '9px',
    fontSize: '13px', color: '#344767', background: '#fff',
    outline: 'none', cursor: 'pointer', minWidth: '0',
  };
  const dateInp = {
    padding: '8px 12px', border: '1.5px solid #e9ecef', borderRadius: '9px',
    fontSize: '13px', color: '#344767', background: '#fff', outline: 'none',
  };
  const lbl = {
    fontSize: '11px', fontWeight: '600', color: '#8392ab',
    textTransform: 'uppercase', letterSpacing: '0.05em',
    marginBottom: '4px', display: 'block', lineHeight: 1.3,
  };

  const quickRangeLabels = {
    today: "Today",
    yesterday: "Yesterday",
    last10days: "Last 10 days",
    thisMonth: "This month",
    lastMonth: "Last month",
    thisYear: "This year",
    lastYear: "Last year",
  };

  const formatDisplayDate = (iso) => {
    if (!iso) return "";
    const d = new Date(`${iso}T12:00:00`);
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  };

  const activeRangeLabel =
    quickRange && quickRange !== "custom"
      ? quickRangeLabels[quickRange] ?? null
      : `${formatDisplayDate(startDate)} – ${formatDisplayDate(endDate)}`;

  /* stat card config */
  const isPrivilegedDashboardUser = roleNumeric !== 4;

  const actionCenterCards = useMemo(
    () => [
      ...(isPrivilegedDashboardUser
        ? [{
            label: "Total Leads",
            value: leadCount,
            color: "#344767",
            icon: "fa-users",
            route: `/filteredLeads/${selectedEmployee}/${startDate}/${endDate}`,
            urgent: false,
          }]
        : []),
      {
        label: "Overdue Follow-Ups",
        value: pendingCount,
        color: "#f5365c",
        icon: "fa-exclamation-circle",
        route: "/pendingfollowups",
        urgent: pendingCount > 0,
      },
      {
        label: "Hot Leads",
        value: hotClientCount,
        color: "#fb6340",
        icon: "fa-fire",
        route: `/filteredDataEmployee/${selectedEmployee}/${startDate}/${endDate}/2`,
        urgent: hotClientCount > 0,
      },
      {
        label: "Upcoming Visits",
        value: upcomingVisitCount,
        color: "#2dce89",
        icon: "fa-calendar-check",
        route: `/filteredDataEmployee/${selectedEmployee}/${startDate}/${endDate}/9`,
        urgent: false,
      },
      {
        label: "Callbacks Pending",
        value: callBackLaterCount,
        color: "#5e72e4",
        icon: "fa-phone-volume",
        route: `/filteredDataEmployee/${selectedEmployee}/${startDate}/${endDate}/8`,
        urgent: callBackLaterCount > 0,
      },
      {
        label: "Assigned Leads",
        value: leadAssignedCount,
        color: "#8965e0",
        icon: "fa-clipboard-list",
        route: `/filteredDashboarddata/${selectedEmployee}/${startDate}/${endDate}`,
        urgent: false,
      },
      {
        label: "Interested",
        value: interestedCount,
        color: "#2dce89",
        icon: "fa-thumbs-up",
        route: `/filteredDataEmployee/${selectedEmployee}/${startDate}/${endDate}/3`,
        urgent: false,
      },
      {
        label: "Visited",
        value: visitedCount,
        color: "#11cdef",
        icon: "fa-map-marker-alt",
        route: `/filteredDataEmployee/${selectedEmployee}/${startDate}/${endDate}/11`,
        urgent: false,
      },
    ],
    [
      isPrivilegedDashboardUser,
      leadCount,
      pendingCount,
      hotClientCount,
      upcomingVisitCount,
      callBackLaterCount,
      leadAssignedCount,
      interestedCount,
      visitedCount,
      selectedEmployee,
      startDate,
      endDate,
    ]
  );

  /* pipeline stage color palette – matches KanbanPipeline */
  const STAGE_COLORS = [
    '#6366f1', '#f59e0b', '#10b981', '#ef4444', '#3b82f6',
    '#8b5cf6', '#fb923c', '#06b6d4', '#00875a', '#e65100',
    '#374151', '#dc2626', '#9ca3af', '#f97316',
  ];

  const sortedEmployeePerf = useMemo(
    () =>
      [...employeePerf].sort(
        (a, b) =>
          (b.totalDealDone ?? b.TotalDealDone ?? 0) - (a.totalDealDone ?? a.TotalDealDone ?? 0)
      ),
    [employeePerf]
  );

  const dealStageOf = (d) => d.stage ?? d.Stage ?? "";
  const dealCounts = useMemo(() => {
    const list = pipelineDeals;
    const active = list.filter((d) => !["ClosedWon", "ClosedLost"].includes(dealStageOf(d))).length;
    const won = list.filter((d) => dealStageOf(d) === "ClosedWon").length;
    const lost = list.filter((d) => dealStageOf(d) === "ClosedLost").length;
    const channel = list.filter(
      (d) => (d.dealSourceType ?? d.DealSourceType) === "ChannelPartner"
    ).length;
    return { total: list.length, active, won, lost, channel };
  }, [pipelineDeals]);

  const filteredDeals = useMemo(() => {
    const srcOf = (d) => d.dealSourceType ?? d.DealSourceType ?? "";
    let rows = pipelineDeals;
    if (dealFilter === "active") {
      rows = rows.filter((d) => !["ClosedWon", "ClosedLost"].includes(dealStageOf(d)));
    } else if (dealFilter === "won") {
      rows = rows.filter((d) => dealStageOf(d) === "ClosedWon");
    } else if (dealFilter === "lost") {
      rows = rows.filter((d) => dealStageOf(d) === "ClosedLost");
    } else if (dealFilter === "channel") {
      rows = rows.filter((d) => srcOf(d) === "ChannelPartner");
    }
    const terminalRank = (s) => (s === "ClosedWon" || s === "ClosedLost" ? 1 : 0);
    return [...rows].sort((a, b) => {
      const sa = dealStageOf(a);
      const sb = dealStageOf(b);
      if (terminalRank(sa) !== terminalRank(sb)) return terminalRank(sa) - terminalRank(sb);
      const ta = new Date(
        a.expectedCloseDate ?? a.ExpectedCloseDate ?? a.closedDate ?? a.ClosedDate ?? 0
      ).getTime();
      const tb = new Date(
        b.expectedCloseDate ?? b.ExpectedCloseDate ?? b.closedDate ?? b.ClosedDate ?? 0
      ).getTime();
      return ta - tb;
    });
  }, [pipelineDeals, dealFilter]);

  const callCards = [
    { label: "Total Calls", value: totalCallsCount, color: "#8965e0", icon: "fa-phone-alt" },
    { label: "Phone Not Picked", value: phoneNotPickedCount, color: "#faad14", icon: "fa-phone-slash" },
    { label: "Invalid Number", value: invalidNumberCount, color: "#f5365c", icon: "fa-times-circle" },
    { label: "Callback Later", value: callBackLaterCount, color: "#722ed1", icon: "fa-reply" },
    { label: "Complete", value: completeCount, color: "#13c2c2", icon: "fa-check-circle" },
  ];


  return (
    <div style={{ padding: '20px', background: '#f4f6fb', minHeight: '100vh' }}>
      <Toast ref={toastRef} position="top-right" />
      <style>{`
        .db-sel:focus,.db-inp:focus{border-color:#344767!important;box-shadow:0 0 0 3px rgba(52,71,103,0.1)!important;outline:none}
        .db-search-btn:hover{opacity:.88;transform:translateY(-1px)}
      `}</style>

      {/* ── Filter bar ── */}
      <div style={{
        background: '#fff', borderRadius: '14px', padding: '14px 18px',
        boxShadow: '0 2px 12px rgba(52,71,103,0.07)', marginBottom: '20px',
        display: 'flex', alignItems: 'flex-end', flexWrap: 'wrap', gap: '12px',
      }}>
        {userRole == Role.SUPERADMIN.roleId && clients.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', minWidth: '180px', flex: '1' }}>
            <span style={lbl}><i className="fas fa-building" style={{ marginRight: 5 }} />Client</span>
            <select
              className="db-sel" style={{ ...sel, borderColor: '#5e72e4', fontWeight: '700' }}
              value={selectedClientId || ''}
              onChange={(e) => setSelectedClientId(Number(e.target.value))}
            >
              {clients.map((c) => (
                <option key={c.id} value={c.id}>{c.agencyName}</option>
              ))}
            </select>
          </div>
        )}

        {isPrivilegedDashboardUser && (
          <div style={{ display: 'flex', flexDirection: 'column', minWidth: '170px', flex: '1' }}>
            <span style={lbl}>Employee</span>
            <select
              className="db-sel" style={sel}
              value={selectedEmployee || ""}
              onChange={(e) => { setSelectedEmployee(e.target.value); handleGetDashboardCards(); }}
            >
              <option value={currentUserId}>Current User</option>
              {employees.map((e) => (
                <option key={e.id} value={e.id}>{e.firstName} {e.lastName}</option>
              ))}
            </select>
          </div>
        )}
        <div style={{ display: 'flex', flexDirection: 'column', minWidth: '150px', flex: '1' }}>
          <span style={lbl}>Quick Range</span>
          <select className="db-sel" style={sel} value={quickRange} onChange={handleDateChange}>
            <option value="today">Today</option>
            <option value="yesterday">Yesterday</option>
            <option value="last10days">Last 10 Days</option>
            <option value="thisMonth">This Month</option>
            <option value="lastMonth">Last Month</option>
            <option value="thisYear">This Year</option>
            <option value="lastYear">Last Year</option>
            <option value="custom">Custom range</option>
          </select>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', minWidth: '140px' }}>
          <span style={lbl}>From</span>
          <input
            className="db-inp"
            type="date"
            style={dateInp}
            value={startDate}
            onChange={(e) => { setStartDate(e.target.value); setQuickRange("custom"); }}
          />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', minWidth: '140px' }}>
          <span style={lbl}>To</span>
          <input
            className="db-inp"
            type="date"
            style={dateInp}
            value={endDate}
            onChange={(e) => { setEndDate(e.target.value); setQuickRange("custom"); }}
          />
        </div>
        {activeRangeLabel && (
          <div style={{ display: 'flex', alignItems: 'center', alignSelf: 'center', paddingBottom: '2px' }}>
            <span style={{
              fontSize: '12px', fontWeight: '600', color: '#5e72e4',
              background: '#5e72e414', borderRadius: '999px', padding: '6px 12px',
              whiteSpace: 'nowrap',
            }}>
              <i className="fas fa-calendar-alt" style={{ marginRight: '6px', fontSize: '11px' }} />
              {activeRangeLabel}
            </span>
          </div>
        )}
        <button
          className="db-search-btn"
          onClick={handleGetDashboardCards}
          style={{
            background: 'linear-gradient(135deg, #344767, #21243d)',
            border: 'none', borderRadius: '9px', padding: '9px 20px',
            color: '#fff', fontWeight: '600', fontSize: '13px', cursor: 'pointer',
            display: 'flex', alignItems: 'center', gap: '7px',
            boxShadow: '0 4px 12px rgba(52,71,103,0.25)', transition: 'all 0.2s',
            alignSelf: 'flex-end',
          }}
        >
          <i className="fas fa-search" /> Search
        </button>
      </div>

      {/* ── Action Center ── */}
      <div style={{ marginBottom: '20px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
          <div style={{ width: '6px', height: '22px', borderRadius: '3px', background: '#f5365c' }} />
          <span style={{ fontWeight: '700', fontSize: '14px', color: '#344767' }}>Action Center</span>
          <span style={{ fontSize: '12px', color: '#8392ab', marginLeft: '4px' }}>— items needing your attention today</span>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '12px' }}>
          {actionCenterCards.map(({ label, value, color, icon, route, urgent }) => (
            <div
              key={label}
              onClick={() => route && navigate(route)}
              style={{
                background: urgent ? color + '10' : '#fff',
                borderTop: `1.5px solid ${urgent ? color + '44' : '#e9ecef'}`,
                borderRight: `1.5px solid ${urgent ? color + '44' : '#e9ecef'}`,
                borderBottom: `1.5px solid ${urgent ? color + '44' : '#e9ecef'}`,
                borderLeft: `4px solid ${color}`,
                borderRadius: '12px',
                padding: '14px 16px',
                cursor: route ? 'pointer' : 'default',
                display: 'flex', alignItems: 'center', gap: '14px',
                transition: 'transform 0.15s, box-shadow 0.15s',
              }}
              onMouseEnter={e => { if (route) { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = `0 4px 16px ${color}22`; }}}
              onMouseLeave={e => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = 'none'; }}
            >
              <div style={{ width: '40px', height: '40px', borderRadius: '10px', background: color + '18', display: 'flex', alignItems: 'center', justifyContent: 'center', color, fontSize: '16px', flexShrink: 0 }}>
                <i className={`fas ${icon}`} />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <p style={{ fontSize: '22px', fontWeight: '800', color, margin: 0, lineHeight: 1 }}>
                  {loading ? <span style={{ fontSize: '14px', color: '#c0c8d8' }}>…</span> : value}
                </p>
                <p style={{ fontSize: '12px', color: '#8392ab', margin: '4px 0 0', fontWeight: '500', lineHeight: 1.25 }}>{label}</p>
              </div>
              {urgent && value > 0 && (
                <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: color, flexShrink: 0, animation: 'pulse 1.5s infinite' }} />
              )}
            </div>
          ))}
        </div>
      </div>
      <style>{`@keyframes pulse{0%,100%{opacity:1;transform:scale(1)}50%{opacity:.5;transform:scale(1.3)}}`}</style>

      {/* ── Deals (transactions) — live pipeline ── */}
      {canViewDealDashboard && clientId ? (
        <div
          style={{
            background: "#fff",
            borderRadius: "14px",
            boxShadow: "0 2px 12px rgba(52,71,103,0.07)",
            padding: dealCounts.total === 0 && !dealsLoading ? "14px" : "18px",
            marginBottom: "20px",
            borderTop: "4px solid #825ee4",
          }}
        >
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              alignItems: "flex-start",
              gap: "12px",
              marginBottom: "14px",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "10px", flex: "1 1 220px" }}>
              <div
                style={{
                  width: "36px",
                  height: "36px",
                  borderRadius: "10px",
                  background: "#825ee422",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#825ee4",
                  fontSize: "15px",
                }}
              >
                <i className="fas fa-file-contract" />
              </div>
              <div>
                <p style={{ fontWeight: "700", color: "#344767", fontSize: "15px", margin: 0 }}>
                  Deals (transactions)
                </p>
                {dealCounts.total > 0 && (
                  <p style={{ fontSize: "11px", color: "#8392ab", margin: "2px 0 0", maxWidth: "520px" }}>
                    View-only tracker for this client — open a buyer profile or Assigned Leads → Properties &amp; Deals to change stages.
                  </p>
                )}
              </div>
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "8px", alignItems: "center" }}>
              <button
                type="button"
                onClick={() => navigate("/buyers")}
                style={{
                  background: "linear-gradient(135deg,#825ee4,#6c4dc9)",
                  border: "none",
                  borderRadius: "9px",
                  padding: "8px 14px",
                  color: "#fff",
                  fontSize: "12px",
                  fontWeight: "600",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                }}
              >
                <i className="fas fa-users" style={{ fontSize: "11px" }} />
                Buyers &amp; deals
              </button>
              <button
                type="button"
                onClick={() => reloadPipelineDeals()}
                disabled={dealsLoading}
                style={{
                  background: "#f4f6fb",
                  border: "1.5px solid #e9ecef",
                  borderRadius: "9px",
                  padding: "8px 12px",
                  color: "#344767",
                  fontSize: "12px",
                  fontWeight: "600",
                  cursor: dealsLoading ? "wait" : "pointer",
                  opacity: dealsLoading ? 0.7 : 1,
                }}
              >
                <i className="fas fa-sync-alt" style={{ marginRight: "6px", fontSize: "11px" }} />
                Refresh
              </button>
            </div>
          </div>

          {dealCounts.total > 0 && (
          <div style={{ display: "flex", flexWrap: "wrap", gap: "8px", marginBottom: "14px" }}>
            {[
              { key: "active", label: "Active pipeline", count: dealCounts.active, color: "#825ee4" },
              { key: "won", label: "Won", count: dealCounts.won, color: "#2dce89" },
              { key: "lost", label: "Lost", count: dealCounts.lost, color: "#f5365c" },
              { key: "channel", label: "Channel partner", count: dealCounts.channel, color: "#fb6340" },
              { key: "all", label: "All", count: dealCounts.total, color: "#344767" },
            ].map(({ key, label, count, color }) => (
              <button
                key={key}
                type="button"
                onClick={() => setDealFilter(key)}
                style={{
                  borderRadius: "999px",
                  padding: "6px 12px",
                  fontSize: "11px",
                  fontWeight: "700",
                  border:
                    dealFilter === key ? `2px solid ${color}` : "1.5px solid #e9ecef",
                  background: dealFilter === key ? color + "14" : "#fff",
                  color: dealFilter === key ? color : "#8392ab",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                }}
              >
                {label}
                <span
                  style={{
                    background: dealFilter === key ? color + "33" : "#f4f6fb",
                    color: dealFilter === key ? color : "#344767",
                    borderRadius: "8px",
                    padding: "1px 7px",
                    fontSize: "10px",
                  }}
                >
                  {count}
                </span>
              </button>
            ))}
          </div>
          )}

          {dealsLoading && pipelineDeals.length === 0 ? (
            <p style={{ margin: 0, fontSize: "13px", color: "#8392ab", padding: "12px 8px", textAlign: "center" }}>
              Loading deals…
            </p>
          ) : filteredDeals.length === 0 ? (
            <div style={{ padding: "14px 12px", textAlign: "center", background: "#f8f9fc", borderRadius: "10px" }}>
              <p style={{ margin: "0 0 10px", fontSize: "13px", color: "#8392ab" }}>
                {dealCounts.total === 0 ? "No deals yet for this client." : "No deals match this filter."}
              </p>
              <button
                type="button"
                onClick={() => navigate("/buyers")}
                style={{
                  background: "linear-gradient(135deg,#825ee4,#6c4dc9)",
                  border: "none",
                  borderRadius: "8px",
                  padding: "7px 14px",
                  color: "#fff",
                  fontSize: "12px",
                  fontWeight: "600",
                  cursor: "pointer",
                }}
              >
                Go to Buyers
              </button>
            </div>
          ) : (
            <div
              style={{
                maxHeight: "380px",
                overflow: "auto",
                border: "1px solid #eef2f7",
                borderRadius: "10px",
              }}
            >
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "12px" }}>
                <thead>
                  <tr style={{ background: "#f8f9fc", position: "sticky", top: 0, zIndex: 1 }}>
                    {["Buyer", "Property / project", "Value", "Deal stage", "Source / commission", ""].map((h) => (
                      <th
                        key={h}
                        style={{
                          textAlign: h === "Value" ? "right" : "left",
                          padding: "10px 12px",
                          fontWeight: "700",
                          color: "#8392ab",
                          borderBottom: "1px solid #eef2f7",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filteredDeals.map((deal) => {
                    const rowId = deal.id ?? deal.Id;
                    const buyerId = deal.buyerId ?? deal.BuyerId;
                    const buyerName = deal.buyerName ?? deal.BuyerName ?? "—";
                    const pname = deal.propertyName ?? deal.PropertyName ?? "";
                    const plabel = deal.projectLabel ?? deal.ProjectLabel ?? "";
                    const propLine = [pname, plabel].filter(Boolean).join(" · ") || "—";
                    const val = deal.dealValue ?? deal.DealValue ?? 0;
                    const stage = dealStageOf(deal);
                    const stageMeta = getDealStage(stage);
                    const src = deal.dealSourceType ?? deal.DealSourceType ?? "";
                    const laId = deal.leadAssignedId ?? deal.LeadAssignedId;
                    const commPct = deal.commissionPercentSnapshot ?? deal.CommissionPercentSnapshot;
                    const expComm = deal.expectedCommissionAmount ?? deal.ExpectedCommissionAmount;
                    const isChannel = src === "ChannelPartner";

                    return (
                      <tr key={rowId} style={{ borderBottom: "1px solid #f0f2f8" }}>
                        <td style={{ padding: "10px 12px", verticalAlign: "top" }}>
                          <button
                            type="button"
                            onClick={() => buyerId && navigate(`/buyers/${buyerId}`)}
                            style={{
                              background: "none",
                              border: "none",
                              padding: 0,
                              color: "#5e72e4",
                              fontWeight: "700",
                              cursor: buyerId ? "pointer" : "default",
                              textAlign: "left",
                              fontSize: "12px",
                            }}
                          >
                            {buyerName}
                          </button>
                          {laId ? (
                            <div style={{ marginTop: "4px" }}>
                              <button
                                type="button"
                                onClick={() => navigate(`/editleadassigned/${laId}`)}
                                style={{
                                  background: "none",
                                  border: "none",
                                  padding: 0,
                                  color: "#8392ab",
                                  fontSize: "10px",
                                  cursor: "pointer",
                                  textDecoration: "underline",
                                }}
                              >
                                Assigned lead
                              </button>
                            </div>
                          ) : null}
                        </td>
                        <td style={{ padding: "10px 12px", color: "#344767", verticalAlign: "top", maxWidth: "220px" }}>
                          <span style={{ fontWeight: "600" }}>{propLine}</span>
                        </td>
                        <td
                          style={{
                            padding: "10px 12px",
                            textAlign: "right",
                            fontWeight: "700",
                            color: "#344767",
                            verticalAlign: "top",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {fmtPrice(val)}
                        </td>
                        <td style={{ padding: "10px 12px", verticalAlign: "top" }}>
                          <span
                            style={{
                              display: "inline-block",
                              fontWeight: "700",
                              fontSize: "11px",
                              padding: "5px 10px",
                              borderRadius: "999px",
                              background: stageMeta.color + "18",
                              color: stageMeta.color,
                              border: `1px solid ${stageMeta.color}44`,
                            }}
                          >
                            {stageMeta.label}
                          </span>
                        </td>
                        <td style={{ padding: "10px 12px", verticalAlign: "top", color: "#8392ab", fontSize: "11px" }}>
                          {isChannel ? (
                            <span>
                              <span
                                style={{
                                  display: "inline-block",
                                  background: "#fb634022",
                                  color: "#fb6340",
                                  fontWeight: "700",
                                  borderRadius: "6px",
                                  padding: "2px 8px",
                                  marginBottom: "4px",
                                }}
                              >
                                Channel
                              </span>
                              <div style={{ color: "#344767", marginTop: "4px" }}>
                                {deal.builderCompanyName ?? deal.BuilderCompanyName ?? "Partner"}
                                {(commPct != null && commPct !== "") && (
                                  <span style={{ color: "#8392ab" }}>
                                    {" "}
                                    · {Number(commPct)}% comm
                                  </span>
                                )}
                                {expComm != null && expComm !== "" && (
                                  <span style={{ display: "block", marginTop: "2px", fontSize: "10px" }}>
                                    Est. payout {fmtPrice(expComm)}
                                  </span>
                                )}
                              </div>
                            </span>
                          ) : (
                            <span style={{ color: "#adb5bd" }}>Direct</span>
                          )}
                        </td>
                        <td style={{ padding: "10px 8px", verticalAlign: "top", textAlign: "right" }}>
                          {buyerId ? (
                            <button
                              type="button"
                              title="Open buyer"
                              onClick={() => navigate(`/buyers/${buyerId}`)}
                              style={{
                                background: "#f4f6fb",
                                border: "1px solid #e9ecef",
                                borderRadius: "8px",
                                padding: "6px 10px",
                                color: "#344767",
                                cursor: "pointer",
                                fontSize: "11px",
                                fontWeight: "600",
                              }}
                            >
                              <i className="fas fa-external-link-alt" />
                            </button>
                          ) : null}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      ) : null}

      {/* ── Deals & Performance Snapshot ── */}
      {canViewDealDashboard && conversionData && (
        <div style={{ background: '#fff', borderRadius: '14px', boxShadow: '0 2px 12px rgba(52,71,103,0.07)', padding: '18px', marginBottom: '20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '14px' }}>
            <div style={{ width: '30px', height: '30px', borderRadius: '8px', background: '#2dce8918', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#2dce89', fontSize: '13px' }}>
              <i className="fas fa-trophy" />
            </div>
            <div>
              <p style={{ fontWeight: '700', color: '#344767', fontSize: '14px', margin: 0 }}>Deals &amp; Performance</p>
              <p style={{ fontSize: '11px', color: '#8392ab', margin: 0 }}>Closed deals for the selected period</p>
            </div>
            <button onClick={() => navigate('/reports')} style={{ marginLeft: 'auto', background: 'linear-gradient(135deg,#2dce89,#26af74)', border: 'none', borderRadius: '8px', padding: '6px 14px', color: '#fff', fontSize: '12px', fontWeight: '600', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '5px' }}>
              <i className="fas fa-chart-bar" style={{ fontSize: '11px' }} /> Reports
            </button>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: '12px' }}>
            {[
              { label: 'Deals Closed', value: conversionData.totalDealDone, color: '#2dce89', icon: 'fa-handshake' },
              { label: 'Payment Pending', value: conversionData.totalPaymentPending, color: '#fb6340', icon: 'fa-clock' },
              { label: 'Total Closed', value: conversionData.totalClosed, color: '#344767', icon: 'fa-check-circle' },
              { label: 'Close Rate', value: `${conversionData.closeRate}%`, color: '#5e72e4', icon: 'fa-percentage' },
              { label: 'Interest Rate', value: `${conversionData.interestRate}%`, color: '#11cdef', icon: 'fa-thumbs-up' },
            ].map(({ label, value, color, icon }) => (
              <div key={label} style={{ background: color + '0d', borderRadius: '12px', padding: '14px 12px', borderTop: `3px solid ${color}`, textAlign: 'center' }}>
                <div style={{ width: '32px', height: '32px', borderRadius: '8px', background: color + '22', display: 'flex', alignItems: 'center', justifyContent: 'center', color, fontSize: '13px', margin: '0 auto 8px' }}>
                  <i className={`fas ${icon}`} />
                </div>
                <p style={{ fontSize: '22px', fontWeight: '800', color, margin: '0 0 3px', lineHeight: 1 }}>{value}</p>
                <p style={{ fontSize: '11px', fontWeight: '600', color: '#8392ab', margin: 0 }}>{label}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Funnel + Today's Follow-Ups ── */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px', marginBottom: '20px' }} className="db-funnel-grid">
        <style>{`@media(max-width:820px){.db-funnel-grid{grid-template-columns:1fr!important}}`}</style>

        {/* Lead Conversion Funnel */}
        <div style={{ background: '#fff', borderRadius: '14px', boxShadow: '0 2px 12px rgba(52,71,103,0.07)', padding: '18px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
            <div style={{ width: '30px', height: '30px', borderRadius: '8px', background: '#5e72e418', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#5e72e4', fontSize: '13px' }}>
              <i className="fas fa-filter" />
            </div>
            <div>
              <p style={{ fontWeight: '700', color: '#344767', fontSize: '14px', margin: 0 }}>Lead Conversion Funnel</p>
              <p style={{ fontSize: '11px', color: '#8392ab', margin: 0 }}>Stage-by-stage pipeline breakdown</p>
            </div>
            <button onClick={() => navigate('/reports')} style={{ marginLeft: 'auto', background: 'none', border: '1px solid #5e72e4', borderRadius: '7px', padding: '4px 10px', color: '#5e72e4', fontSize: '11px', fontWeight: '600', cursor: 'pointer' }}>
              Full Report
            </button>
          </div>
          {conversionData ? (() => {
            const stages = [
              { label: 'Total Leads', value: conversionData.totalLeads, color: '#344767' },
              { label: 'Assigned', value: conversionData.totalAssigned, color: '#5e72e4' },
              { label: 'Contacted', value: conversionData.totalContacted, color: '#1890ff' },
              { label: 'Interested', value: conversionData.totalInterested, color: '#2dce89' },
              { label: 'Visited', value: conversionData.totalVisited, color: '#fb6340' },
              { label: 'Deal Done', value: conversionData.totalDealDone, color: '#f5365c' },
            ];
            const maxVal = stages[0].value || 1;
            return (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {stages.map((s, i) => {
                  const pct = Math.round((s.value / maxVal) * 100);
                  const convPct = i > 0 && stages[i - 1].value > 0
                    ? Math.round((s.value / stages[i - 1].value) * 100)
                    : null;
                  return (
                    <div key={s.label}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '4px' }}>
                        <span style={{ fontSize: '12px', fontWeight: '600', color: '#525f7f' }}>{s.label}</span>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          {convPct !== null && (
                            <span style={{ fontSize: '10px', color: convPct >= 50 ? '#2dce89' : convPct >= 25 ? '#fb6340' : '#f5365c', fontWeight: '600', background: convPct >= 50 ? '#2dce8920' : convPct >= 25 ? '#fb634020' : '#f5365c20', padding: '1px 6px', borderRadius: '8px' }}>
                              {convPct}% conv.
                            </span>
                          )}
                          <span style={{ fontSize: '13px', fontWeight: '700', color: s.color, minWidth: '28px', textAlign: 'right' }}>{s.value}</span>
                        </div>
                      </div>
                      <div style={{ background: '#f0f2f5', borderRadius: '6px', height: '10px', overflow: 'hidden' }}>
                        <div style={{ width: `${pct}%`, height: '100%', background: s.color, borderRadius: '6px', transition: 'width 0.6s ease' }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })() : (
            <div style={{ color: '#8392ab', fontSize: '13px', textAlign: 'center', padding: '24px 0' }}>Loading funnel data…</div>
          )}
        </div>

        {/* Today's Follow-Ups */}
        <div style={{ background: '#fff', borderRadius: '14px', boxShadow: '0 2px 12px rgba(52,71,103,0.07)', padding: '18px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
            <div style={{ width: '30px', height: '30px', borderRadius: '8px', background: '#f5365c18', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#f5365c', fontSize: '13px' }}>
              <i className="fas fa-bell" />
            </div>
            <div>
              <p style={{ fontWeight: '700', color: '#344767', fontSize: '14px', margin: 0 }}>Urgent Follow-Ups</p>
              <p style={{ fontSize: '11px', color: '#8392ab', margin: 0 }}>Overdue &amp; pending action</p>
            </div>
            <button onClick={() => navigate('/pendingfollowups')} style={{ marginLeft: 'auto', background: 'none', border: '1px solid #f5365c', borderRadius: '7px', padding: '4px 10px', color: '#f5365c', fontSize: '11px', fontWeight: '600', cursor: 'pointer' }}>
              View All ({pendingCount})
            </button>
          </div>
          {todayFollowUps.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '24px 0' }}>
              <div style={{ width: '40px', height: '40px', borderRadius: '50%', background: '#2dce8918', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 8px', color: '#2dce89', fontSize: '18px' }}>
                <i className="fas fa-check" />
              </div>
              <p style={{ color: '#2dce89', fontWeight: '600', fontSize: '13px', margin: 0 }}>All caught up!</p>
              <p style={{ color: '#8392ab', fontSize: '11px', margin: '4px 0 0' }}>No overdue follow-ups right now</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {todayFollowUps.map((item, i) => {
                const name = item?.lead?.firstName || item?.Lead?.FirstName || `Lead #${item?.leadId || item?.LeadId || i + 1}`;
                const phone = item?.lead?.contactNumber || item?.Lead?.ContactNumber || '';
                const status = item?.leadStatus?.leadStatusName || item?.LeadStatus?.LeadStatusName || '';
                const followUp = item?.followUpDate || item?.FollowUpDate;
                const daysOverdue = followUp ? Math.floor((new Date() - new Date(followUp)) / 86400000) : 0;
                const isUrgent = daysOverdue > 1;
                return (
                  <div
                    key={i}
                    onClick={() => navigate(`/editleadassigned/${item?.id || item?.Id}`)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: '12px',
                      padding: '10px 12px', borderRadius: '10px', cursor: 'pointer',
                      background: isUrgent ? '#f5365c08' : '#f8f9fe',
                      border: `1px solid ${isUrgent ? '#f5365c22' : '#f0f2f5'}`,
                      transition: 'background 0.15s',
                    }}
                  >
                    <div style={{ width: '36px', height: '36px', borderRadius: '50%', background: isUrgent ? '#f5365c18' : '#5e72e418', display: 'flex', alignItems: 'center', justifyContent: 'center', color: isUrgent ? '#f5365c' : '#5e72e4', fontSize: '13px', fontWeight: '700', flexShrink: 0 }}>
                      {name.charAt(0).toUpperCase()}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <p style={{ fontSize: '13px', fontWeight: '600', color: '#344767', margin: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{name}</p>
                      <p style={{ fontSize: '11px', color: '#8392ab', margin: '2px 0 0' }}>{phone || status}</p>
                    </div>
                    {daysOverdue > 0 && (
                      <span style={{ fontSize: '10px', fontWeight: '700', color: isUrgent ? '#f5365c' : '#fb6340', background: isUrgent ? '#f5365c15' : '#fb634015', padding: '2px 7px', borderRadius: '8px', flexShrink: 0, whiteSpace: 'nowrap' }}>
                        {daysOverdue}d overdue
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* ── Hot Leads · Activity Feed · Team Leaderboard ── */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '14px', marginBottom: '20px' }} className="db-bottom-grid">
        <style>{`@media(max-width:1024px){.db-bottom-grid{grid-template-columns:1fr 1fr!important}}@media(max-width:640px){.db-bottom-grid{grid-template-columns:1fr!important}}`}</style>

        {/* Hot Leads */}
        <div style={{ background: '#fff', borderRadius: '14px', boxShadow: '0 2px 12px rgba(52,71,103,0.07)', padding: '18px', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '14px' }}>
            <div style={{ width: '30px', height: '30px', borderRadius: '8px', background: '#f5365c18', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#f5365c', fontSize: '13px' }}>
              <i className="fas fa-fire" />
            </div>
            <div>
              <p style={{ fontWeight: '700', color: '#344767', fontSize: '14px', margin: 0 }}>Hot Leads</p>
              <p style={{ fontSize: '11px', color: '#8392ab', margin: 0 }}>Top by pipeline score</p>
            </div>
          </div>
          {hotLeads.length === 0 ? (
            <p style={{ color: '#8392ab', fontSize: '13px', textAlign: 'center', padding: '20px 0' }}>No lead data yet</p>
          ) : (
            <>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '9px' }}>
                {hotLeads.slice(0, PREVIEW_ROWS).map((lead, i) => {
                  const score = lead.leadScore ?? lead.LeadScore ?? 0;
                  const name  = lead.leadName ?? lead.LeadName ?? `Lead #${lead.leadId}`;
                  const status = lead.leadStatusName ?? lead.LeadStatusName ?? '';
                  const agent  = lead.assignedTo ?? lead.AssignedTo ?? '';
                  const lastAct = lead.lastActivity ?? lead.LastActivity;
                  const daysAgo = lastAct ? Math.floor((new Date() - new Date(lastAct)) / 86400000) : null;
                  const scoreColor = score >= 80 ? '#f5365c' : score >= 60 ? '#fb6340' : score >= 40 ? '#faad14' : '#8392ab';
                  return (
                    <div
                      key={lead.leadAssignedId ?? lead.LeadAssignedId ?? `hl-${i}`}
                      onClick={() => navigate(`/editleadassigned/${lead.leadAssignedId ?? lead.LeadAssignedId}`)}
                      style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '9px 10px', borderRadius: '10px', background: '#f8f9fe', cursor: 'pointer', border: '1px solid #f0f2f5', transition: 'background 0.15s' }}
                      onMouseEnter={e => { e.currentTarget.style.background = '#f0f2fb'; }}
                      onMouseLeave={e => { e.currentTarget.style.background = '#f8f9fe'; }}
                    >
                      <div style={{ position: 'relative', width: '40px', height: '40px', flexShrink: 0 }}>
                        <svg width="40" height="40" style={{ transform: 'rotate(-90deg)' }}>
                          <circle cx="20" cy="20" r="16" fill="none" stroke="#f0f2f5" strokeWidth="3.5" />
                          <circle cx="20" cy="20" r="16" fill="none" stroke={scoreColor} strokeWidth="3.5"
                            strokeDasharray={`${(score / 100) * 100.5} 100.5`} strokeLinecap="round" />
                        </svg>
                        <span style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '10px', fontWeight: '800', color: scoreColor }}>{score}</span>
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <p style={{ fontSize: '12px', fontWeight: '700', color: '#344767', margin: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{name}</p>
                        <p style={{ fontSize: '10px', color: '#8392ab', margin: '2px 0 0', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {status}{agent ? ` · ${agent}` : ''}
                        </p>
                      </div>
                      {daysAgo !== null && (
                        <span style={{ fontSize: '10px', color: '#8392ab', flexShrink: 0 }}>{daysAgo === 0 ? 'Today' : `${daysAgo}d ago`}</span>
                      )}
                    </div>
                  );
                })}
              </div>
              {hotLeads.length > PREVIEW_ROWS && (
                <button
                  type="button"
                  onClick={() => setDashModal('hot')}
                  style={{
                    marginTop: '12px', padding: '8px 4px', background: 'none', border: 'none',
                    color: '#5e72e4', fontWeight: '700', fontSize: '12px', cursor: 'pointer', textAlign: 'center',
                  }}
                >
                  See more <span style={{ fontWeight: '500', color: '#8392ab' }}>— past 7 days</span>
                </button>
              )}
            </>
          )}
        </div>

        {/* Activity Feed */}
        <div style={{ background: '#fff', borderRadius: '14px', boxShadow: '0 2px 12px rgba(52,71,103,0.07)', padding: '18px', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '14px' }}>
            <div style={{ width: '30px', height: '30px', borderRadius: '8px', background: '#11cdef18', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#11cdef', fontSize: '13px' }}>
              <i className="fas fa-stream" />
            </div>
            <div>
              <p style={{ fontWeight: '700', color: '#344767', fontSize: '14px', margin: 0 }}>Activity Feed</p>
              <p style={{ fontSize: '11px', color: '#8392ab', margin: 0 }}>Latest pipeline events</p>
            </div>
          </div>
          {recentActivity.length === 0 ? (
            <p style={{ color: '#8392ab', fontSize: '13px', textAlign: 'center', padding: '20px 0' }}>No activity recorded yet</p>
          ) : (
            <>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0' }}>
                {recentActivity.slice(0, PREVIEW_ROWS).map((act, i) => {
                  const previewLen = Math.min(recentActivity.length, PREVIEW_ROWS);
                  const name   = act.leadName ?? act.LeadName ?? `Lead #${act.leadId}`;
                  const status = act.statusName ?? act.StatusName ?? '';
                  const comment = act.comment ?? act.Comment ?? '';
                  const time   = act.activityTime ?? act.ActivityTime;
                  const daysAgo = time ? Math.floor((new Date() - new Date(time)) / 86400000) : null;
                  const timeStr = daysAgo === 0 ? 'Today' : daysAgo === 1 ? 'Yesterday' : daysAgo !== null ? `${daysAgo}d ago` : '';
                  const dotColors = ['#5e72e4','#2dce89','#f5365c','#fb6340','#11cdef','#8965e0','#faad14'];
                  const dotColor = dotColors[i % dotColors.length];
                  return (
                    <div
                      key={`${act.leadId ?? act.LeadId}-${time}-${i}`}
                      onClick={() => navigate(`/leads/${act.leadId ?? act.LeadId}`)}
                      style={{ display: 'flex', gap: '12px', padding: '9px 0', borderBottom: i < previewLen - 1 ? '1px solid #f0f2f5' : 'none', cursor: 'pointer' }}
                    >
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0, paddingTop: '3px' }}>
                        <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: dotColor }} />
                        {i < previewLen - 1 && <div style={{ width: '1.5px', flex: 1, background: '#f0f2f5', marginTop: '3px' }} />}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <p style={{ fontSize: '12px', fontWeight: '600', color: '#344767', margin: 0 }}>
                          <span style={{ color: dotColor }}>{name}</span>
                          {status ? <> moved to <span style={{ color: '#525f7f' }}>{status}</span></> : null}
                        </p>
                        {comment && <p style={{ fontSize: '11px', color: '#8392ab', margin: '2px 0 0', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{comment}</p>}
                        <p style={{ fontSize: '10px', color: '#c0c8d8', margin: '2px 0 0' }}>{timeStr}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
              {recentActivity.length > PREVIEW_ROWS && (
                <button
                  type="button"
                  onClick={() => setDashModal('activity')}
                  style={{
                    marginTop: '12px', padding: '8px 4px', background: 'none', border: 'none',
                    color: '#5e72e4', fontWeight: '700', fontSize: '12px', cursor: 'pointer', textAlign: 'center',
                  }}
                >
                  See more <span style={{ fontWeight: '500', color: '#8392ab' }}>— past 7 days</span>
                </button>
              )}
            </>
          )}
        </div>

        {/* Team Leaderboard */}
        <div style={{ background: '#fff', borderRadius: '14px', boxShadow: '0 2px 12px rgba(52,71,103,0.07)', padding: '18px', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '14px' }}>
            <div style={{ width: '30px', height: '30px', borderRadius: '8px', background: '#faad1418', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#faad14', fontSize: '13px' }}>
              <i className="fas fa-trophy" />
            </div>
            <div>
              <p style={{ fontWeight: '700', color: '#344767', fontSize: '14px', margin: 0 }}>Top Performers</p>
              <p style={{ fontSize: '11px', color: '#8392ab', margin: 0 }}>Ranked by deals closed</p>
            </div>
          </div>
          {sortedEmployeePerf.length === 0 ? (
            <p style={{ color: '#8392ab', fontSize: '13px', textAlign: 'center', padding: '20px 0' }}>No employee data for this period</p>
          ) : (
            <>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {sortedEmployeePerf.slice(0, PREVIEW_ROWS).map((emp, i) => {
                  const deals   = emp.totalDealDone   ?? emp.TotalDealDone   ?? 0;
                  const assigned= emp.totalAssigned   ?? emp.TotalAssigned   ?? 0;
                  const name    = emp.employeeName    ?? emp.EmployeeName    ?? `Employee ${i + 1}`;
                  const rate    = emp.conversionRate  ?? emp.ConversionRate  ?? 0;
                  const medals  = ['#faad14', '#8392ab', '#fb6340'];
                  const rankColor = i < 3 ? medals[i] : '#c0c8d8';
                  const barPct  = assigned > 0 ? Math.round((deals / assigned) * 100) : 0;
                  const initial = name.charAt(0).toUpperCase();
                  return (
                    <div key={emp.userId ?? emp.UserId ?? `emp-${i}`} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 10px', borderRadius: '10px', background: i === 0 ? '#faad1408' : '#f8f9fe', border: `1px solid ${i === 0 ? '#faad1430' : '#f0f2f5'}` }}>
                      <div style={{ width: '22px', textAlign: 'center', fontWeight: '800', fontSize: i < 3 ? '14px' : '12px', color: rankColor, flexShrink: 0 }}>
                        {i < 3 ? ['🥇','🥈','🥉'][i] : `#${i + 1}`}
                      </div>
                      <div style={{ width: '34px', height: '34px', borderRadius: '50%', background: `hsl(${(i * 67 + 200) % 360},55%,88%)`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '13px', fontWeight: '700', color: `hsl(${(i * 67 + 200) % 360},50%,40%)`, flexShrink: 0 }}>
                        {initial}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span style={{ fontSize: '12px', fontWeight: '600', color: '#344767', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '90px' }}>{name}</span>
                          <span style={{ fontSize: '12px', fontWeight: '800', color: rankColor, flexShrink: 0 }}>{deals} deals</span>
                        </div>
                        <div style={{ background: '#f0f2f5', borderRadius: '4px', height: '5px', marginTop: '5px', overflow: 'hidden' }}>
                          <div style={{ width: `${barPct}%`, height: '100%', background: rankColor, borderRadius: '4px', transition: 'width 0.6s ease' }} />
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '2px' }}>
                          <span style={{ fontSize: '10px', color: '#c0c8d8' }}>{assigned} assigned</span>
                          <span style={{ fontSize: '10px', color: '#8392ab' }}>{rate}% conv.</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              {sortedEmployeePerf.length > PREVIEW_ROWS && (
                <button
                  type="button"
                  onClick={() => setDashModal('performers')}
                  style={{
                    marginTop: '12px', padding: '8px 4px', background: 'none', border: 'none',
                    color: '#5e72e4', fontWeight: '700', fontSize: '12px', cursor: 'pointer', textAlign: 'center',
                  }}
                >
                  See more <span style={{ fontWeight: '500', color: '#8392ab' }}>— past 7 days</span>
                </button>
              )}
            </>
          )}
        </div>
      </div>

      {dashModal && (() => {
        const { startStr, endStr } = rollingSevenDayBounds();
        const rangeLabel = `${startStr} → ${endStr}`;
        const modalTitles = { hot: "Hot Leads", activity: "Activity Feed", performers: "Top Performers" };
        const mh = modalTitles[dashModal];
        return (
          <div
            role="presentation"
            style={{
              position: "fixed", inset: 0, zIndex: 10050,
              background: "rgba(52,71,103,0.52)", display: "flex", alignItems: "center",
              justifyContent: "center", padding: 16,
            }}
            onClick={() => setDashModal(null)}
          >
            <div
              role="dialog"
              aria-modal="true"
              aria-labelledby="dash-modal-title"
              style={{
                background: "#fff", borderRadius: 16, maxWidth: 560, width: "100%",
                maxHeight: "82vh", overflow: "hidden", display: "flex", flexDirection: "column",
                boxShadow: "0 12px 48px rgba(52,71,103,0.22)",
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div style={{ padding: "16px 20px", borderBottom: "1px solid #f0f2f5", display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12 }}>
                <div>
                  <p id="dash-modal-title" style={{ margin: 0, fontWeight: "800", fontSize: "16px", color: "#344767" }}>{mh}</p>
                  <p style={{ margin: "6px 0 0", fontSize: "12px", color: "#8392ab" }}>
                    Past 7 days ({rangeLabel})
                  </p>
                </div>
                <button
                  type="button"
                  aria-label="Close"
                  onClick={() => setDashModal(null)}
                  style={{
                    flexShrink: 0, width: 36, height: 36, borderRadius: 10, border: "1px solid #e9ecef",
                    background: "#f8f9fe", cursor: "pointer", fontSize: 20, lineHeight: 1, color: "#525f7f",
                  }}
                >
                  ×
                </button>
              </div>
              <div style={{ overflow: "auto", padding: 16, flex: 1 }}>
                {dashModalLoading ? (
                  <div style={{ textAlign: "center", padding: "36px 16px", color: "#8392ab" }}>
                    <i className="fas fa-circle-notch fa-spin" style={{ marginRight: 8 }} />
                    Loading…
                  </div>
                ) : dashModal === "hot" ? (
                  dashModalHot.length === 0 ? (
                    <p style={{ color: "#8392ab", fontSize: "14px", textAlign: "center", padding: "24px 8px", margin: 0 }}>
                      No hot leads with activity in the past 7 days.
                    </p>
                  ) : (
                    <div style={{ display: "flex", flexDirection: "column", gap: "9px" }}>
                      {dashModalHot.map((lead, i) => {
                        const score = lead.leadScore ?? lead.LeadScore ?? 0;
                        const name = lead.leadName ?? lead.LeadName ?? `Lead #${lead.leadId}`;
                        const status = lead.leadStatusName ?? lead.LeadStatusName ?? "";
                        const agent = lead.assignedTo ?? lead.AssignedTo ?? "";
                        const lastAct = lead.lastActivity ?? lead.LastActivity;
                        const daysAgo = lastAct ? Math.floor((new Date() - new Date(lastAct)) / 86400000) : null;
                        const scoreColor = score >= 80 ? "#f5365c" : score >= 60 ? "#fb6340" : score >= 40 ? "#faad14" : "#8392ab";
                        return (
                          <div
                            key={lead.leadAssignedId ?? lead.LeadAssignedId ?? `mh-${i}`}
                            onClick={() => navigate(`/editleadassigned/${lead.leadAssignedId ?? lead.LeadAssignedId}`)}
                            style={{
                              display: "flex", alignItems: "center", gap: "10px", padding: "9px 10px", borderRadius: "10px",
                              background: "#f8f9fe", cursor: "pointer", border: "1px solid #f0f2f5",
                            }}
                          >
                            <div style={{ position: "relative", width: "40px", height: "40px", flexShrink: 0 }}>
                              <svg width="40" height="40" style={{ transform: "rotate(-90deg)" }}>
                                <circle cx="20" cy="20" r="16" fill="none" stroke="#f0f2f5" strokeWidth="3.5" />
                                <circle cx="20" cy="20" r="16" fill="none" stroke={scoreColor} strokeWidth="3.5"
                                  strokeDasharray={`${(score / 100) * 100.5} 100.5`} strokeLinecap="round" />
                              </svg>
                              <span style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", fontSize: "10px", fontWeight: "800", color: scoreColor }}>{score}</span>
                            </div>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <p style={{ fontSize: "12px", fontWeight: "700", color: "#344767", margin: 0 }}>{name}</p>
                              <p style={{ fontSize: "10px", color: "#8392ab", margin: "2px 0 0" }}>
                                {status}{agent ? ` · ${agent}` : ""}
                              </p>
                            </div>
                            {daysAgo !== null && (
                              <span style={{ fontSize: "10px", color: "#8392ab", flexShrink: 0 }}>{daysAgo === 0 ? "Today" : `${daysAgo}d ago`}</span>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )
                ) : dashModal === "activity" ? (
                  dashModalActivity.length === 0 ? (
                    <p style={{ color: "#8392ab", fontSize: "14px", textAlign: "center", padding: "24px 8px", margin: 0 }}>
                      No pipeline activity in the past 7 days.
                    </p>
                  ) : (
                    <div style={{ display: "flex", flexDirection: "column", gap: 0 }}>
                      {dashModalActivity.map((act, i) => {
                        const name = act.leadName ?? act.LeadName ?? `Lead #${act.leadId}`;
                        const status = act.statusName ?? act.StatusName ?? "";
                        const comment = act.comment ?? act.Comment ?? "";
                        const time = act.activityTime ?? act.ActivityTime;
                        const daysAgo = time ? Math.floor((new Date() - new Date(time)) / 86400000) : null;
                        const timeStr = daysAgo === 0 ? "Today" : daysAgo === 1 ? "Yesterday" : daysAgo !== null ? `${daysAgo}d ago` : "";
                        const dotColors = ["#5e72e4", "#2dce89", "#f5365c", "#fb6340", "#11cdef", "#8965e0", "#faad14"];
                        const dotColor = dotColors[i % dotColors.length];
                        const last = i === dashModalActivity.length - 1;
                        return (
                          <div
                            key={`${act.leadId}-${time}-${i}`}
                            onClick={() => navigate(`/leads/${act.leadId ?? act.LeadId}`)}
                            style={{
                              display: "flex", gap: "12px", padding: "9px 0",
                              borderBottom: last ? "none" : "1px solid #f0f2f5", cursor: "pointer",
                            }}
                          >
                            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", flexShrink: 0, paddingTop: "3px" }}>
                              <div style={{ width: "8px", height: "8px", borderRadius: "50%", background: dotColor }} />
                              {!last && <div style={{ width: "1.5px", flex: 1, background: "#f0f2f5", marginTop: "3px", minHeight: 12 }} />}
                            </div>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <p style={{ fontSize: "12px", fontWeight: "600", color: "#344767", margin: 0 }}>
                                <span style={{ color: dotColor }}>{name}</span>
                                {status ? <> moved to <span style={{ color: "#525f7f" }}>{status}</span></> : null}
                              </p>
                              {comment && <p style={{ fontSize: "11px", color: "#8392ab", margin: "4px 0 0", wordBreak: "break-word" }}>{comment}</p>}
                              <p style={{ fontSize: "10px", color: "#c0c8d8", margin: "4px 0 0" }}>{timeStr}</p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )
                ) : dashModalPerf.length === 0 ? (
                  <p style={{ color: "#8392ab", fontSize: "14px", textAlign: "center", padding: "24px 8px", margin: 0 }}>
                    No performance data for the past 7 days.
                  </p>
                ) : (
                  <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                    {dashModalPerf.map((emp, i) => {
                      const deals = emp.totalDealDone ?? emp.TotalDealDone ?? 0;
                      const assigned = emp.totalAssigned ?? emp.TotalAssigned ?? 0;
                      const name = emp.employeeName ?? emp.EmployeeName ?? `Employee ${i + 1}`;
                      const rate = emp.conversionRate ?? emp.ConversionRate ?? 0;
                      const medals = ["#faad14", "#8392ab", "#fb6340"];
                      const rankColor = i < 3 ? medals[i] : "#c0c8d8";
                      const barPct = assigned > 0 ? Math.round((deals / assigned) * 100) : 0;
                      const initial = name.charAt(0).toUpperCase();
                      return (
                        <div
                          key={emp.userId ?? emp.UserId ?? `mp-${i}`}
                          style={{
                            display: "flex", alignItems: "center", gap: "10px", padding: "8px 10px", borderRadius: "10px",
                            background: i === 0 ? "#faad1408" : "#f8f9fe", border: `1px solid ${i === 0 ? "#faad1430" : "#f0f2f5"}`,
                          }}
                        >
                          <div style={{ width: "22px", textAlign: "center", fontWeight: "800", fontSize: i < 3 ? "14px" : "12px", color: rankColor, flexShrink: 0 }}>
                            {i < 3 ? ["🥇", "🥈", "🥉"][i] : `#${i + 1}`}
                          </div>
                          <div style={{
                            width: "34px", height: "34px", borderRadius: "50%",
                            background: `hsl(${(i * 67 + 200) % 360},55%,88%)`, display: "flex", alignItems: "center",
                            justifyContent: "center", fontSize: "13px", fontWeight: "700",
                            color: `hsl(${(i * 67 + 200) % 360},50%,40%)`, flexShrink: 0,
                          }}>
                            {initial}
                          </div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                              <span style={{ fontSize: "12px", fontWeight: "600", color: "#344767" }}>{name}</span>
                              <span style={{ fontSize: "12px", fontWeight: "800", color: rankColor, flexShrink: 0 }}>{deals} deals</span>
                            </div>
                            <div style={{ background: "#f0f2f5", borderRadius: "4px", height: "5px", marginTop: "5px", overflow: "hidden" }}>
                              <div style={{ width: `${barPct}%`, height: "100%", background: rankColor, borderRadius: "4px" }} />
                            </div>
                            <div style={{ display: "flex", justifyContent: "space-between", marginTop: "2px" }}>
                              <span style={{ fontSize: "10px", color: "#c0c8d8" }}>{assigned} assigned</span>
                              <span style={{ fontSize: "10px", color: "#8392ab" }}>{rate}% conv.</span>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      })()}

      {/* ── Pipeline Stage Cards ── */}
      {pipelineStages.length > 0 && (
        <div style={{ background: '#fff', borderRadius: '14px', boxShadow: '0 2px 12px rgba(52,71,103,0.07)', padding: '18px', marginBottom: '20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
            <div style={{ width: '30px', height: '30px', borderRadius: '8px', background: '#6366f118', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#6366f1', fontSize: '13px' }}>
              <i className="fas fa-columns" />
            </div>
            <div>
              <p style={{ fontWeight: '700', color: '#344767', fontSize: '14px', margin: 0 }}>Pipeline Overview</p>
              <p style={{ fontSize: '11px', color: '#8392ab', margin: 0 }}>Leads across all pipeline stages</p>
            </div>
            <button
              onClick={() => navigate('/kanban')}
              style={{
                marginLeft: 'auto', background: 'linear-gradient(135deg,#6366f1,#8b5cf6)',
                border: 'none', borderRadius: '8px', padding: '6px 14px',
                color: '#fff', fontSize: '12px', fontWeight: '600', cursor: 'pointer',
                display: 'flex', alignItems: 'center', gap: '5px',
              }}
            >
              <i className="fas fa-columns" style={{ fontSize: '11px' }} /> Kanban View
            </button>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: '10px' }}>
            {pipelineStages.map((stage, idx) => {
              const color = stage.color || STAGE_COLORS[idx % STAGE_COLORS.length];
              const bg = color + '18';
              const cnt = stageCounts[stage.id] ?? 0;
              return (
                <div
                  key={stage.id}
                  onClick={() => navigate('/kanban')}
                  style={{
                    background: bg,
                    borderRadius: '12px',
                    padding: '14px 12px',
                    borderTop: `3px solid ${color}`,
                    cursor: 'pointer',
                    transition: 'transform 0.15s, box-shadow 0.15s',
                    textAlign: 'center',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = `0 6px 18px ${color}33`; }}
                  onMouseLeave={e => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = 'none'; }}
                >
                  <p style={{ fontSize: '26px', fontWeight: '800', color, margin: '0 0 4px', lineHeight: 1 }}>
                    {loading ? <span style={{ fontSize: '14px', color: '#c0c8d8' }}>…</span> : cnt}
                  </p>
                  <p style={{ fontSize: '11px', fontWeight: '600', color, margin: 0, opacity: 0.85, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {stage.leadStatusName}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── Attendance Analytics (non-employee) ── */}
      {userRole !== 4 && (
        <AttendanceAnalytics todayAttendance={todayAttendance} employeePerf={employeePerf} />
      )}

      {/* ── Charts + Call stats ── */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '14px', marginBottom: '20px' }} className="db-charts-grid">
        <style>{`@media(max-width:900px){.db-charts-grid{grid-template-columns:1fr!important}}`}</style>

        {/* Call records chart */}
        <div style={{ background: '#fff', borderRadius: '14px', boxShadow: '0 2px 12px rgba(52,71,103,0.07)', padding: '18px', display: 'flex', flexDirection: 'column' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '14px' }}>
            <div style={{ width: '30px', height: '30px', borderRadius: '8px', background: '#8965e018', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#8965e0', fontSize: '13px' }}>
              <i className="fas fa-phone-alt" />
            </div>
            <div>
              <p style={{ fontWeight: '700', color: '#344767', fontSize: '14px', margin: 0 }}>Call Records</p>
              <p style={{ fontSize: '11px', color: '#8392ab', margin: 0 }}>Calls activity breakdown</p>
            </div>
          </div>
          <div style={{ flex: 1 }}>
            <CallsLineGraph
              totalCallsCount={totalCallsCount}
              phoneNotPickedCount={phoneNotPickedCount}
              invalidNumberCount={invalidNumberCount}
              callBackLaterCount={callBackLaterCount}
              completeCount={completeCount}
            />
          </div>
        </div>

        {/* Pie chart */}
        <div style={{ background: '#fff', borderRadius: '14px', boxShadow: '0 2px 12px rgba(52,71,103,0.07)', padding: '18px', display: 'flex', flexDirection: 'column' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '14px' }}>
            <div style={{ width: '30px', height: '30px', borderRadius: '8px', background: '#5e72e418', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#5e72e4', fontSize: '13px' }}>
              <i className="fas fa-chart-pie" />
            </div>
            <div>
              <p style={{ fontWeight: '700', color: '#344767', fontSize: '14px', margin: 0 }}>Lead & Call Statistics</p>
              <p style={{ fontSize: '11px', color: '#8392ab', margin: 0 }}>Distribution overview</p>
            </div>
          </div>
          <div style={{ flex: 1 }}>
            <PieChart
              leadCount={leadCount}
              leadAssignedCount={leadAssignedCount}
              upcomingVisitCount={upcomingVisitCount}
              interestedCount={interestedCount}
              totalCallsCount={totalCallsCount}
            />
          </div>
        </div>

        {/* Call breakdown mini cards */}
        <div style={{ background: '#fff', borderRadius: '14px', boxShadow: '0 2px 12px rgba(52,71,103,0.07)', padding: '18px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '14px' }}>
            <div style={{ width: '30px', height: '30px', borderRadius: '8px', background: '#fb634018', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fb6340', fontSize: '13px' }}>
              <i className="fas fa-list-ul" />
            </div>
            <div>
              <p style={{ fontWeight: '700', color: '#344767', fontSize: '14px', margin: 0 }}>Call Breakdown</p>
              <p style={{ fontSize: '11px', color: '#8392ab', margin: 0 }}>Detailed call status counts</p>
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {callCards.map(({ label, value, color, icon }) => (
              <div key={label} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 12px', background: '#f8f9fe', borderRadius: '10px' }}>
                <div style={{ width: '34px', height: '34px', borderRadius: '9px', background: color + '18', display: 'flex', alignItems: 'center', justifyContent: 'center', color, fontSize: '13px', flexShrink: 0 }}>
                  <i className={`fas ${icon}`} />
                </div>
                <span style={{ fontSize: '13px', color: '#525f7f', flex: 1, fontWeight: '500' }}>{label}</span>
                <span style={{ fontSize: '18px', fontWeight: '700', color: '#344767' }}>
                  {loading ? <span style={{ fontSize: '12px', color: '#c0c8d8' }}>…</span> : value}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>


    </div>
  );
}

// ─── Attendance Analytics component (keeps Dashboard.js clean, no new file needed) ─────────
const LATE_THRESHOLD_HOUR = 9;   // employees who check in after 9:30 AM are "late"
const LATE_THRESHOLD_MIN  = 30;

function AttendanceAnalytics({ todayAttendance, employeePerf }) {
  if (!todayAttendance || todayAttendance.length === 0) return null;

  // ── Derived counts ────────────────────────────────────────────────────────
  const total   = todayAttendance.length;
  const present = todayAttendance.filter(a => a.status === 'Present' || a.status === 'WorkFromHome').length;
  const absent  = todayAttendance.filter(a => a.status === 'Absent').length;
  const onLeave = todayAttendance.filter(a => a.status === 'Leave' || a.status === 'HalfDay').length;
  const wfh     = todayAttendance.filter(a => a.status === 'WorkFromHome').length;
  const lateArr = todayAttendance.filter(a => {
    if (!a.checkIn) return false;
    const d = new Date(a.checkIn);
    return d.getHours() > LATE_THRESHOLD_HOUR ||
           (d.getHours() === LATE_THRESHOLD_HOUR && d.getMinutes() > LATE_THRESHOLD_MIN);
  });
  const availPct = total ? Math.round((present / total) * 100) : 0;

  const card = { background: '#fff', borderRadius: 14, boxShadow: '0 2px 12px rgba(52,71,103,.07)', padding: 18 };
  const badge = (color) => ({
    display: 'inline-block', padding: '2px 10px', borderRadius: 20,
    fontSize: 11, fontWeight: 700, color: '#fff', background: color,
  });

  // productivity = lead performance from employeePerf joined with todayAttendance
  const prodRows = todayAttendance
    .filter(a => a.status === 'Present' || a.status === 'WorkFromHome')
    .map(a => {
      const perf = employeePerf.find(p => p.userId === a.userId) || {};
      return { ...a, conversions: perf.conversions || 0, calls: perf.calls || 0, leads: perf.leadsHandled || perf.leadsCount || 0 };
    })
    .sort((x, y) => y.conversions - x.conversions)
    .slice(0, 5);

  // status distribution for bar
  const statusDist = ATTENDANCE_STATUSES.map(s => ({
    ...s,
    count: todayAttendance.filter(a => a.status === s.id).length,
  })).filter(s => s.count > 0);

  return (
    <div style={{ marginBottom: 20 }}>
      {/* Section header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
        <div style={{ width: 32, height: 32, borderRadius: 9, background: '#5e72e418', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#5e72e4', fontSize: 14 }}>
          <i className="fas fa-calendar-check" />
        </div>
        <div>
          <p style={{ fontWeight: 700, color: '#344767', fontSize: 15, margin: 0 }}>Attendance Analytics</p>
          <p style={{ fontSize: 11, color: '#8392ab', margin: 0 }}>
            Today — {new Date().toLocaleDateString('en-IN', { weekday: 'long', day: '2-digit', month: 'short' })}
          </p>
        </div>
      </div>

      {/* Row 1: 5 quick-stat mini cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(130px,1fr))', gap: 12, marginBottom: 14 }}>
        {[
          { label: 'Total Staff', val: total,    color: '#5e72e4', icon: 'fa-users' },
          { label: 'Present',     val: present,  color: '#2dce89', icon: 'fa-check-circle' },
          { label: 'Absent',      val: absent,   color: '#f5365c', icon: 'fa-times-circle' },
          { label: 'On Leave',    val: onLeave,  color: '#fb6340', icon: 'fa-umbrella-beach' },
          { label: 'Late Today',  val: lateArr.length, color: '#ffd600', icon: 'fa-clock' },
        ].map(({ label, val, color, icon }) => (
          <div key={label} style={{ ...card, textAlign: 'center', borderTop: `3px solid ${color}` }}>
            <i className={`fas ${icon}`} style={{ color, fontSize: 20, marginBottom: 6 }} />
            <p style={{ margin: 0, fontSize: 11, color: '#8392ab', fontWeight: 600 }}>{label}</p>
            <p style={{ margin: '3px 0 0', fontSize: 24, fontWeight: 800, color: '#344767' }}>{val}</p>
          </div>
        ))}
      </div>

      {/* Row 2: Availability ring + Status bar + Late comers */}
      <div style={{ display: 'grid', gridTemplateColumns: '160px 1fr 1fr', gap: 14, marginBottom: 14 }} className="att-row2">
        <style>{`.att-row2{@media(max-width:900px){grid-template-columns:1fr!important}}`}</style>

        {/* Availability ring */}
        <div style={{ ...card, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center' }}>
          <div style={{ position: 'relative', width: 100, height: 100 }}>
            <svg viewBox="0 0 36 36" style={{ transform: 'rotate(-90deg)', width: 100, height: 100 }}>
              <circle cx="18" cy="18" r="15.9" fill="none" stroke="#f0f2f5" strokeWidth="3.5" />
              <circle cx="18" cy="18" r="15.9" fill="none" stroke="#2dce89" strokeWidth="3.5"
                strokeDasharray={`${availPct} ${100 - availPct}`} strokeLinecap="round" />
            </svg>
            <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
              <span style={{ fontSize: 20, fontWeight: 800, color: '#344767' }}>{availPct}%</span>
            </div>
          </div>
          <p style={{ margin: '8px 0 2px', fontWeight: 700, color: '#344767', fontSize: 13 }}>Team Available</p>
          <p style={{ margin: 0, fontSize: 11, color: '#8392ab' }}>{present} of {total} present</p>
        </div>

        {/* Status distribution bar */}
        <div style={card}>
          <p style={{ margin: '0 0 12px', fontWeight: 700, color: '#344767', fontSize: 13 }}>
            <i className="fas fa-chart-bar" style={{ color: '#5e72e4', marginRight: 6 }} />Status Breakdown
          </p>
          {statusDist.map(s => (
            <div key={s.id} style={{ marginBottom: 10 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                <span style={{ fontSize: 12, color: '#525f7f', fontWeight: 600 }}>
                  <i className={s.icon} style={{ color: s.color, marginRight: 5 }} />{s.label}
                </span>
                <span style={{ fontSize: 12, fontWeight: 700, color: '#344767' }}>{s.count}</span>
              </div>
              <div style={{ background: '#f0f2f5', borderRadius: 6, height: 7, overflow: 'hidden' }}>
                <div style={{ width: `${Math.round((s.count / total) * 100)}%`, height: '100%', background: s.color, borderRadius: 6, transition: 'width .5s' }} />
              </div>
            </div>
          ))}
          {wfh > 0 && (
            <p style={{ margin: '8px 0 0', fontSize: 11, color: '#8392ab' }}>
              <i className="fas fa-home" style={{ marginRight: 4, color: '#5e72e4' }} />
              {wfh} working remotely today
            </p>
          )}
        </div>

        {/* Late comers list */}
        <div style={card}>
          <p style={{ margin: '0 0 12px', fontWeight: 700, color: '#344767', fontSize: 13 }}>
            <i className="fas fa-clock" style={{ color: '#ffd600', marginRight: 6 }} />Late Comers
            {lateArr.length > 0 && <span style={{ ...badge('#ffd600'), marginLeft: 8 }}>{lateArr.length}</span>}
          </p>
          {lateArr.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '16px 0' }}>
              <i className="fas fa-star" style={{ color: '#2dce89', fontSize: 22 }} />
              <p style={{ margin: '6px 0 0', fontSize: 12, color: '#2dce89', fontWeight: 600 }}>Everyone on time!</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {lateArr.slice(0, 5).map(a => {
                const ci = new Date(a.checkIn);
                const lateMin = (ci.getHours() - LATE_THRESHOLD_HOUR) * 60 + (ci.getMinutes() - LATE_THRESHOLD_MIN);
                const initials = (a.employeeName || 'UN').split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();
                return (
                  <div key={a.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', background: '#fff9e6', borderRadius: 8, border: '1px solid #ffd60033' }}>
                    <div style={{ width: 32, height: 32, borderRadius: '50%', background: '#ffd600', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#344767', fontWeight: 800, fontSize: 12, flexShrink: 0 }}>
                      {initials}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <p style={{ margin: 0, fontSize: 12, fontWeight: 700, color: '#344767', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.employeeName}</p>
                      <p style={{ margin: 0, fontSize: 11, color: '#8392ab' }}>In: {fmtTime(a.checkIn)}</p>
                    </div>
                    <span style={{ fontSize: 11, fontWeight: 700, color: '#fb6340', whiteSpace: 'nowrap' }}>+{lateMin}m late</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Row 3: Productivity vs Attendance table */}
      {prodRows.length > 0 && (
        <div style={card}>
          <p style={{ margin: '0 0 14px', fontWeight: 700, color: '#344767', fontSize: 13 }}>
            <i className="fas fa-chart-line" style={{ color: '#2dce89', marginRight: 6 }} />
            Productivity vs Attendance — Today's Present Staff
          </p>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: '#f8f9fe' }}>
                  {['Employee', 'Status', 'Check In', 'Working Hrs', 'Leads', 'Calls', 'Conversions'].map(h => (
                    <th key={h} style={{ padding: '9px 12px', textAlign: 'left', color: '#8392ab', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {prodRows.map((row, idx) => {
                  const st = getAttendanceStatus(row.status);
                  const workH = row.workingMinutes ? `${Math.floor(row.workingMinutes / 60)}h ${row.workingMinutes % 60}m` : '—';
                  const initials = (row.employeeName || 'UN').split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();
                  const colors = ['#5e72e4','#2dce89','#fb6340','#11cdef','#825ee4'];
                  const av = colors[idx % colors.length];
                  return (
                    <tr key={row.id} style={{ borderBottom: '1px solid #f0f2f5', background: idx % 2 === 0 ? '#fafbff' : '#fff' }}>
                      <td style={{ padding: '10px 12px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <div style={{ width: 30, height: 30, borderRadius: '50%', background: av, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 800, flexShrink: 0 }}>{initials}</div>
                          <span style={{ fontWeight: 600, color: '#344767', fontSize: 13 }}>{row.employeeName}</span>
                        </div>
                      </td>
                      <td style={{ padding: '10px 12px' }}>
                        <span style={{ background: st.color + '22', color: st.color, borderRadius: 12, padding: '2px 9px', fontSize: 11, fontWeight: 700 }}>
                          <i className={st.icon} style={{ marginRight: 3 }} />{st.label}
                        </span>
                      </td>
                      <td style={{ padding: '10px 12px', color: '#8392ab', fontSize: 12 }}>{fmtTime(row.checkIn)}</td>
                      <td style={{ padding: '10px 12px', fontWeight: 700, color: row.workingMinutes >= 480 ? '#2dce89' : '#fb6340' }}>{workH}</td>
                      <td style={{ padding: '10px 12px', fontWeight: 700, color: '#344767' }}>{row.leads || '—'}</td>
                      <td style={{ padding: '10px 12px', fontWeight: 700, color: '#344767' }}>{row.calls || '—'}</td>
                      <td style={{ padding: '10px 12px' }}>
                        {row.conversions > 0
                          ? <span style={{ background: '#2dce8922', color: '#2dce89', borderRadius: 12, padding: '2px 10px', fontSize: 12, fontWeight: 800 }}>{row.conversions}</span>
                          : <span style={{ color: '#c0c8d8' }}>—</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

export default Dashboard;
